import React, {
  useCallback,
  useRef,
  useState,
  useEffect,
  ReactNode,
} from "react";
import {
  Button,
  CircularProgress,
  Link,
  styled,
  TextField,
} from "@mui/material";
import {
  authenticateUser,
  resendConfirmationCodeFn,
  SIGN_IN_STATUS,
} from "../cognito";
import { GoogleButton } from "./googleButton";
import { ACTION, CATEGORY, evt } from "../common/analytics";
import { SupportedLanguages } from "@kahunmedical/common-configuration/dist/configuration/supportedLanguages";
import { KAHUN_PLATFORM } from "./constants";
import VerificationCode from "./verificationCode";
import ErrorMessage from "./ErrorMessage.tsx";

interface LoginFormProps {
  onSignIn: (email?: string) => void;
  onError?: (error: ReactNode) => void;
  disabled?: boolean;
  keepLoading?: boolean;
  onLoading?: () => void;
  onLoadingDone?: () => void;
  platform?: KAHUN_PLATFORM;
  clinic?: string;
  locale?: string;
  enableGoogleLogin?: boolean;
  socialLoginRedirect?: string;
  showErrors?: boolean;
  trackEvents?: boolean;
}
const LoginButton = styled(Button)({
  marginTop: 10,
});
const OrSeparator = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  width: "100%",
  padding: "8px 5px",
  gap: 10,
  fontSize: 12,
  fontWeight: 700,
  "& hr": {
    borderColor: theme.palette.divider,
    borderWidth: "1px 0 0 0",
    borderStyle: "solid",
    flex: 1,
  },
}));

export const LoginForm = ({
  onLoading,
  onLoadingDone,
  keepLoading,
  onSignIn,
  onError,
  clinic = null,
  locale = SupportedLanguages.English_US,
  platform = KAHUN_PLATFORM.UNKNOWN,
  enableGoogleLogin = true,
  socialLoginRedirect,
  showErrors = true,
  trackEvents = true,
}: LoginFormProps) => {
  const passwordRef = useRef<HTMLInputElement>();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [verificationRequired, setVerificationRequired] = useState(false);
  const [loadingState, setLoadingState] = useState(false);
  const [error, setError] = useState<ReactNode>("");

  /* Sync controlled props with sate */
  useEffect(() => {
    setLoadingState(keepLoading);
  }, [keepLoading, setLoadingState]);

  /* Keep loading state synced with parent if needed */
  const setLoading = useCallback(
    (state: boolean) => {
      if (state && onLoading) return onLoading();
      if (!state && onLoadingDone) return onLoadingDone();
      // Let parent update the state if we are controlled
      if (keepLoading || keepLoading === false) return;
      setLoadingState(state);
    },
    [onLoading, onLoadingDone, keepLoading, setLoadingState],
  );

  const handleError = useCallback(
    (error: ReactNode) => {
      setError(error);
      onError && onError(error);
    },
    [onError],
  );

  const handleSendCode = useCallback(async () => {
    try {
      await resendConfirmationCodeFn(email);
    } catch (e) {
      console.error(e);
      handleError(e.message);
    }
  }, [email, handleError]);

  const handleSignIn = useCallback(() => {
    if (!email || !password) return;
    handleError("");
    setLoading(true);
    (async () => {
      try {
        await authenticateUser(email, password);
        if (trackEvents) {
          evt({
            category: CATEGORY.USER_AUTHENTICATION,
            action: ACTION.LOG_IN,
            props: {
              platform,
              userId: email,
              clinic,
              locale,
            },
          });
        }
        onSignIn && onSignIn(email);
      } catch (err) {
        switch (err) {
          case SIGN_IN_STATUS.CONFIRM_SIGN_IN_MISSING:
            handleError(
              "User exists, but not verified. Use the verification code sent to your email",
            );
            setVerificationRequired(true);
            break;
          case SIGN_IN_STATUS.RESET_PASSWORD:
            handleError(
              <span>
                User exists, but password reset is required. Please check your
                email for instructions.
                <Link
                  onClick={handleSendCode}
                  sx={{ marginLeft: "5px", cursor: "pointer" }}
                >
                  Resend code
                </Link>
              </span>,
            );
            break;
          default:
            handleError(err);
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [
    email,
    password,
    setLoading,
    trackEvents,
    onSignIn,
    platform,
    clinic,
    locale,
    handleError,
    handleSendCode,
  ]);

  const handleEmailChange = (e) => setEmail(e.target.value);

  const handlePasswordChange = (e) => setPassword(e.target.value);

  const handleEnterInEmail = (e) => {
    if ((e.key === "Enter" || e.Key === "F12") && passwordRef.current) {
      passwordRef.current.focus();
    }
  };

  const handleEnterInPassword = (e) => {
    if (e.key === "Enter" || e.Key === "F12") {
      handleSignIn();
    }
  };

  return (
    <>
      <TextField
        className={"email-input"}
        name="username"
        margin="normal"
        variant="outlined"
        label="Email Address"
        type="email"
        autoComplete="username"
        fullWidth
        required
        onChange={handleEmailChange}
        onKeyDown={handleEnterInEmail}
      />
      <TextField
        className={"password-input"}
        ref={passwordRef}
        name="password"
        margin="normal"
        variant="outlined"
        label="Password"
        type="password"
        autoComplete="password"
        fullWidth
        required
        onChange={handlePasswordChange}
        onKeyDown={handleEnterInPassword}
      />
      <LoginButton
        variant="contained"
        fullWidth
        color="primary"
        type="button"
        onClick={handleSignIn}
        disabled={loadingState}
        className="submit-button"
      >
        {loadingState ? <CircularProgress size={20} /> : "Log In"}
      </LoginButton>

      {enableGoogleLogin && (
        <>
          <OrSeparator className={"or-separator"}>
            <hr />
            Or
            <hr />
          </OrSeparator>
          <GoogleButton
            redirectTo={socialLoginRedirect}
            onError={handleError}
          />
        </>
      )}
      {verificationRequired && (
        <VerificationCode
          onVerificationComplete={() => handleSignIn()}
          onError={handleError}
          email={email}
        />
      )}
      {error && showErrors && <ErrorMessage error={error} />}
    </>
  );
};
